import * as React from "react";
import { NavLink as Link } from 'react-router-dom';
import { Trans } from "@components/i18n/typedTrans";
import { ITranslationKeys } from "@components/i18n/keys";
import { useTranslation } from '@lib/useTypedTranslation';
import { Tooltip } from '@components/tooltip/tooltip';
import { useSidebarTabs } from './useSidebarTabs';
import './selectorBar.css';

export interface ISelectorBarTab {
  title: string,
  id: string,
  link: string,
  icon?: string | React.ComponentType<React.SVGProps<SVGSVGElement>>,
  disabled?: boolean
}

export const SelectorBar = () => {
  const { tabs, initialSelection, enabled } = useSidebarTabs();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const sidebarRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  React.useEffect(() => {
    if (!enabled) setIsExpanded(false);
  }, [enabled]);

  // .page (the generic page wrapper used by every route, Core or not) needs extra margin to clear
  // this fixed-position sidebar - but only while the sidebar is actually on screen. Scoping by
  // which page wrapper rendered the content isn't enough: a licence-denied render inside a /core/*
  // route still uses the plain StandardPage wrapper, yet the sidebar is still visible since the URL
  // still matches. Toggling a class on <body> (see appWrapper.tsx's browser-* class for the same
  // pattern) keys the CSS off real sidebar visibility instead.
  React.useEffect(() => {
    document.body.classList.toggle('has-selector-bar', enabled);
    return () => { document.body.classList.remove('has-selector-bar'); };
  }, [enabled]);

  if (!enabled) return null;

  const toggleLabel = t(isExpanded ? 'HIDE_TEXT_LABELS' : 'SHOW_TEXT_LABELS');
  const toggleButton = (
    <button
      className="selector-bar-toggle"
      onClick={() => setIsExpanded(prev => !prev)}
      aria-label={toggleLabel}
    >
      <span className="selector-button-icon"><i className={`fas ${isExpanded ? 'fa-angles-left' : 'fa-angles-right'}`} /></span>
      <span className="selector-button-label">{toggleLabel}</span>
    </button>
  );

  return (
    <div ref={sidebarRef} className="selector-bar" data-is-expanded={isExpanded}>
      {!isExpanded
        ? <Tooltip popperContent={toggleLabel} placement="right" popoverPortalSelector="body" className="selector-bar-tooltip" showDelayMs={200}>{toggleButton}</Tooltip>
        : toggleButton}
      {tabs.map(item => {
        const label = <Trans i18nKey={item.title as keyof ITranslationKeys['translation']} ns="translation">{item.title}</Trans>;
        const buttonContent = (
          <span id={item.id} data-id={item.id} data-disabled={item.disabled} className={`selector-button ${initialSelection === item.title ? 'active' : ''} ${item.disabled ? 'disabled' : ''}`}>
            {item.icon && (
              <span className="selector-button-icon">
                {typeof item.icon === 'string' ? <i className={item.icon} /> : <item.icon />}
              </span>
            )}
            <span className="selector-button-label">{label}</span>
          </span>
        );
        const link = item.disabled
          ? <span className="selector-bar-link" aria-disabled="true">{buttonContent}</span>
          : (
            <Link to={item.link} className="selector-bar-link" onClick={() => setIsExpanded(false)}>
              {buttonContent}
            </Link>
          );
        const tooltipContent = item.disabled
          ? <Trans i18nKey="NOT_INCLUDED_IN_LICENCE" ns="translation">Not included in your licence</Trans>
          : label;
        return !isExpanded
          ? <Tooltip key={item.id} popperContent={tooltipContent} placement="right" popoverPortalSelector="body" className="selector-bar-tooltip" showDelayMs={200}>{link}</Tooltip>
          : <React.Fragment key={item.id}>{link}</React.Fragment>;
      })}
    </div>
  );
};
